<template>
  <c-box class="support_request">
    <c-heading as="h1" fontSize="4xl" fontWeight="normal" marginTop="4xl" marginBottom="2xl">{{
      $t(`tickets.newTicket`)
    }}</c-heading>

    <c-box
      bg="white"
      :mx="{ base: '-15px', md: '0' }"
      :border-radius="{ md: 'base' }"
      :box-shadow="{ md: 'base' }"
      :px="{ base: '15px', md: 'lg', lg: '3xl', xl: '4xl' }"
      :py="{ base: 'lg', md: 'lg', lg: 'xl', xl: '2.5rem' }"
    >
      <form @submit="submitRequest" novalidate>
        <c-box>
          <c-box class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('form.ticketRoutingInfo')">
              <c-box
                display="flex"
                position="relative"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
              >
                <c-form-label for="ticket-category-0" :min-width="{ lg: '12rem' }">
                  {{ $t("tickets.ticketCategory") }}
                </c-form-label>
                <c-form-label
                  v-for="(cat, catIdx) in categories
                    .slice(0, categories.length - 1)
                    .filter((cat) => cat !== null)"
                  v-bind:key="cat.name"
                  class="screen-reader-only"
                >
                  {{ $t("tickets.ticketSubcategory") }} {{ catIdx + 1 }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-box>
                    <c-select
                      id="ticket-category-0"
                      :placeholder="$t('tickets.ticketCategory.placeholderForMainCategory')"
                      size="lg"
                      border-radius="input"
                      v-model="categoryNames[0]"
                      data-cy="ticket-category-0"
                      :border-color="categoryNames[0] !== null ? 'inherit !important' : undefined"
                      :box-shadow="categoryNames[0] !== null ? 'none !important' : undefined"
                      :color="!categoryNames[0] ? 'brand.grayDark' : undefined"
                    >
                      <option
                        v-for="catName in getCategoryNames(getTicketCategories())"
                        v-bind:key="catName"
                        :value="catName"
                      >
                        {{ $t(`tickets.ticketCategory.${catName}`) }}
                      </option>
                    </c-select>
                  </c-box>
                  <c-box
                    v-for="(cat, catIdx) in categories.slice(0, categories.length - 1)"
                    v-bind:key="cat?.name"
                  >
                    <c-select
                      v-if="cat !== null && cat.subCategories"
                      :id="`ticket-category-${catIdx + 1}`"
                      :placeholder="$t('tickets.ticketCategory.placeholderForSubcategory')"
                      size="lg"
                      border-radius="input"
                      mt="xs"
                      v-model="categoryNames[catIdx + 1]"
                      :data-cy="`ticket-category-${catIdx + 1}`"
                      :border-color="
                        categoryNames[catIdx + 1] !== null ? 'inherit !important' : undefined
                      "
                      :box-shadow="
                        categoryNames[catIdx + 1] !== null ? 'none !important' : undefined
                      "
                      :color="!categoryNames[catIdx + 1] ? 'brand.grayDark' : undefined"
                      ariaLabel="`Subcategory ${catIdx + 1}`"
                    >
                      <option
                        v-for="subCatName in getCategoryNames(cat.subCategories)"
                        v-bind:key="subCatName"
                        :value="subCatName"
                      >
                        {{ $t(`tickets.ticketCategory.${subCatName}`) }}
                      </option>
                    </c-select>
                  </c-box>
                  <c-box
                    v-if="isErrorField('form.ticketRoutingInfo')"
                    color="brand.red"
                    font-size="sm"
                    mt="xs"
                    data-cy="ticket-category-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("tickets.ticketCategory"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>

          <c-box class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('form.subject')">
              <c-box
                display="flex"
                position="relative"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
              >
                <c-form-label for="subject" :min-width="{ lg: '12rem' }">
                  {{ $t("tickets.topic") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    id="subject"
                    data-cy="newRequestTopic"
                    v-model.trim="form.subject"
                    size="lg"
                    @keyup="onFieldChange('form.subject')"
                    @blur="onFieldBlur('form.subject')"
                  />
                  <c-box
                    v-if="isErrorField('form.subject')"
                    color="brand.red"
                    font-size="sm"
                    mt="xs"
                    data-cy="subject-error"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("tickets.topic"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>

          <c-box
            v-for="fieldName in dynamicTicketFields.requiredAdditionalInfos"
            v-bind:key="fieldName"
            class="form-group"
          >
            <c-form-control is-required :is-invalid="inputIsInvalid(`form.${fieldName}`)">
              <c-box
                display="flex"
                position="relative"
                :flex-direction="{ base: 'column', sm: 'row' }"
                align-items="baseline"
              >
                <c-form-label :for="`dynamic.${fieldName}`" :min-width="{ lg: '12rem' }">
                  {{ $t(`tickets.dynamicField.${fieldName}`) }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <c-input
                    :id="`dynamic.${fieldName}`"
                    v-model.trim="form[fieldName]"
                    size="lg"
                    @keyup="onFieldChange(`form.${fieldName}`)"
                    @blur="onFieldBlur(`form.${fieldName}`)"
                  />
                  <c-box
                    v-if="isErrorField(`form.${fieldName}`)"
                    color="brand.red"
                    font-size="sm"
                    mt="xs"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t(`tickets.dynamicField.${fieldName}`),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>

          <c-box
            v-for="instruction in dynamicTicketFields.instructionTexts"
            v-bind:key="instruction"
            my="md"
            font-weight="normal"
          >
            <label>{{ $t(`tickets.dynamicInstruction.${instruction}`) }}</label>
          </c-box>

          <c-box class="form-group">
            <c-form-control is-required :is-invalid="inputIsInvalid('form.description')">
              <c-box
                display="flex"
                position="relative"
                :flex-direction="{ base: 'column', lg: 'row' }"
                align-items="baseline"
              >
                <c-form-label :min-width="{ lg: '12rem' }" for="description">
                  {{ $t("tickets.message") }}
                </c-form-label>
                <c-box flex="1" width="100%">
                  <!-- @keyup and @blur seem not to be working in c-textarea, added watcher function descriptionChanged() as workaround -->
                  <c-textarea
                    id="description"
                    v-model="form.description"
                    min-height="180px"
                    data-cy="newRequestMessage"
                  />
                  <c-box
                    v-if="isErrorField('form.description')"
                    color="brand.red"
                    font-size="sm"
                    mt="xs"
                  >
                    {{
                      $t("common.error.fieldRequired", {
                        field: $t("tickets.message"),
                      })
                    }}
                  </c-box>
                </c-box>
              </c-box>
            </c-form-control>
          </c-box>

          <c-box v-if="dynamicTicketFields.requiredAttachments.length" my="md" font-weight="normal">
            {{ $t("tickets.requiredAttachments") }}
            <c-list styleType="disc" pl="md">
              <c-list-item
                v-for="attachment in dynamicTicketFields.requiredAttachments"
                v-bind:key="attachment"
              >
                {{ $t(`tickets.requiredAttachment.${attachment}`) }}
              </c-list-item>
            </c-list>
          </c-box>

          <FileUpload :get-attachments="handleAttachments" />
          <c-box
            v-if="
              dynamicTicketFields.requiredAttachments.length > 0 && isErrorField('form.attachments')
            "
            class="invalid-feedback"
          >
            {{ $t("tickets.validations.attachRequiredFiles") }}
          </c-box>
        </c-box>
        <c-flex justify="start" mt="3xl">
          <c-button
            :disabled="submittingRequest"
            variation="solid"
            background="brand.beige"
            borderColor="brand.beige"
            size="lg"
            type="submit"
            data-cy="submitRequest"
          >
            {{ $t("tickets.submitTicket") }}
          </c-button>
        </c-flex>
      </form>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import FileUpload from "@/components/FileUpload.vue";
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import Modal from "@/components/Modal.vue";
import Button from "@/design-system/elements/Button.vue";
import i18n from "@/i18n";
import { ISolutionsState } from "@/store/solutionsModule";
import {
  TicketCategory,
  getAllPossibleDynamicFieldsAsObjectWithEmptyValues,
  getCategoryByName,
  getCategoryNames,
  getSelectedFullCategoryPath,
  ticketCategories,
} from "@/util/SupportTicketUtil";
import {
  CBox,
  CButton,
  CFlex,
  CFormControl,
  CFormLabel,
  CHeading,
  CIcon,
  CInput,
  CList,
  CListItem,
  CSelect,
  CText,
  CTextarea,
} from "@chakra-ui/vue";
import {
  ISupportRequestField,
  ISupportRequestInput,
  ITicketRoutingInfo,
  Logger,
  IRootState,
} from "@iloq/shared";
import Vue, { defineComponent } from "vue";
import { minLength, required } from "vuelidate/lib/validators";
import { mapState } from "vuex";

export default defineComponent({
  name: "SupportRequest",
  mixins: [FormValidationMixin],
  components: {
    CIcon,
    CText,
    CBox,
    CButton,
    CFlex,
    CFormLabel,
    CFormControl,
    CHeading,
    CInput,
    CList,
    CListItem,
    CSelect,
    CTextarea,
    Button,
    FileUpload,
    "dialog-modal": Modal,
  },
  data() {
    return {
      form: {
        partnerId: "",
        subject: "",
        description: "",
        email: "",
        attachments: [],
        ticketRoutingInfo: ITicketRoutingInfo.empty,
        ...getAllPossibleDynamicFieldsAsObjectWithEmptyValues(),
      },
      categoryNames: [null, null, null] as (string | null)[],
      prevCategoryNames: [] as (string | null)[],
      categories: [null, null, null] as (TicketCategory | null)[],
      submittingRequest: false,
    };
  },
  computed: {
    ...mapState("solutions", {
      supportRequests: (state: unknown) => (state as ISolutionsState).supportRequests,
    }),
    ...mapState({ user: (state: unknown) => (state as IRootState).user! }),
    dynamicTicketFields(): {
      requiredAdditionalInfos: string[];
      requiredAttachments: string[];
      instructionTexts: string[];
    } {
      const result = {
        requiredAdditionalInfos: [] as string[],
        requiredAttachments: [] as string[],
        instructionTexts: [] as string[],
      };
      (getSelectedFullCategoryPath(this.categories) || []).forEach((category) => {
        result.requiredAdditionalInfos.push(...(category.requiredAdditionalInfos || []));
        result.requiredAttachments.push(...(category.requiredAttachments || []));
        result.instructionTexts.push(...(category.instructionTexts || []));
      });
      return result;
    },
  },
  validations() {
    return {
      form: {
        partnerId: { required },
        subject: { required },
        description: { required },
        email: { required },
        ticketRoutingInfo: { required },
        ...(this as any).getDynamicValidations((this as any).categories),
      },
    };
  },
  mounted() {
    this.form.partnerId = this.user.partnerId!;
    this.form.email = this.user.email;
  },
  methods: {
    getCategoryNames,
    getTicketCategories() {
      return ticketCategories;
    },
    getDynamicValidations(categories: (TicketCategory | null)[]) {
      const result: any = {};
      const selectedFullCategoryPath = getSelectedFullCategoryPath(categories) || [];
      selectedFullCategoryPath.forEach((category) => {
        (category.requiredAdditionalInfos || []).forEach((fieldName) => {
          result[fieldName] = { required };
        });
      });
      if (
        selectedFullCategoryPath.find((category) => (category.requiredAttachments || []).length > 0)
      ) {
        result.attachments = {
          required,
          minLength: minLength(1),
        };
      }
      return result;
    },
    handleAttachments(files: File[]) {
      this.form.attachments = files;
      if (this.dynamicTicketFields.requiredAttachments.length) {
        this.onFieldChange("form.attachments");
      }
    },
    submitRequest(e: any) {
      e.preventDefault();
      (this as any).$v.$touch();
      if ((this as any).$v.$invalid) {
        Logger.warn("Validation errors:"); // Log something here?
        return;
      }
      this.submittingRequest = true;
      const input = this.form as ISupportRequestInput;
      const data: ISupportRequestInput = {
        categories: this.getTranslatedCategories(),
        fields: this.getTranslatedFields(),
        subject: input.subject,
        description: input.description,
        attachments: input.attachments,
        ticketRoutingInfo: input.ticketRoutingInfo,
      };
      this.$store
        .dispatch("solutions/createSupportTicket", data)
        .then((success: boolean) => {
          if (success) {
            this.$store.commit("showGeneralInfo", {
              dialogTitle: this.$t("tickets.ticketCreated.title"),
              dialogText: this.$t("tickets.ticketCreated.message"),
            });
            this.$router.push({ name: "support_requests" });
          }
          this.submittingRequest = false;
        })
        .catch(() => {
          this.$store.commit("showGeneralError", {
            dialogTitle: this.$t("tickets.ticketCreationFailed.title"),
            dialogText: this.$t("tickets.ticketCreationFailed.message"),
          });
          this.submittingRequest = false;
        });
    },
    getTranslatedCategories(): string[] {
      return (
        getSelectedFullCategoryPath(this.categories)!.map(
          (category) => i18n.t(`tickets.ticketCategory.${category.name}`) as string,
        ) || ([] as string[])
      );
    },
    getTranslatedFields(): ISupportRequestField[] {
      return this.dynamicTicketFields.requiredAdditionalInfos.map((fieldName) => ({
        label: i18n.t(`tickets.dynamicField.${fieldName}`) as string,
        value: this.form[fieldName],
      }));
    },
    inputIsInvalid(inputName: string): boolean {
      return this.getFieldClasses(inputName)?.["is-invalid"] === true;
    },
    watchPartner() {
      this.form.partnerId = this.user.partnerId!;
      this.form.email = this.user.email;
    },
    categoryChanged() {
      // This gets called also when the data is not changing; detect real changes only
      if (JSON.stringify(this.prevCategoryNames) !== JSON.stringify(this.categoryNames)) {
        let changedCategoryIndex = this.categoryNames.findIndex(
          (value, index) => value !== this.prevCategoryNames[index],
        );
        if (!this.categoryNames[changedCategoryIndex]) {
          // Selection cleared
          changedCategoryIndex--; // Causes also current category be nullified in the for loop below
          if (this.form.ticketRoutingInfo !== ITicketRoutingInfo.empty) {
            this.form.ticketRoutingInfo = ITicketRoutingInfo.empty;
            this.onFieldBlur("form.ticketRoutingInfo");
          }
        } else {
          const changedCategory = getCategoryByName(this.categoryNames[changedCategoryIndex]!);
          Vue.set(this.categories, changedCategoryIndex, changedCategory);

          if (changedCategory.ticketRoutingInfo) {
            this.form.ticketRoutingInfo = changedCategory.ticketRoutingInfo;
            this.onFieldBlur("form.ticketRoutingInfo");
          } else {
            if (this.form.ticketRoutingInfo !== ITicketRoutingInfo.empty) {
              this.form.ticketRoutingInfo = ITicketRoutingInfo.empty;
              this.onFieldBlur("form.ticketRoutingInfo");
            }
          }
        }

        for (let i = changedCategoryIndex + 1; i < this.categories.length; i++) {
          Vue.set(this.categories, i, null);
          Vue.set(this.categoryNames, i, null);
        }

        this.prevCategoryNames = [...this.categoryNames];
      }
    },
    descriptionChanged() {
      this.onFieldChange("form.description");
    },
  },
  watch: {
    categoryNames: "categoryChanged",
    user: "watchPartner",
    "form.description": "descriptionChanged",
  },
});
</script>
